import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faUsers, 
    faComments, 
    faBullhorn,
    faPray, 
    faUserMd,
    faBuilding,
    faChevronDown,
    faChevronUp,
    faPlus,
    faBell,
    faExternalLinkAlt,
    faExclamationTriangle,
    faDownload,
    faEye,
    faTrophy,
    faMedal,
    faCrown,
    faStar,
    faUserPlus,
    faUserMinus,
    faUser,
    faTrash,
    faEdit,
    faCog,
    faCopy,
    faQrcode
} from '@fortawesome/free-solid-svg-icons';
import { useParams, useNavigate } from 'react-router-dom';
import Header from './header';
import Footer from './footer';
import { useCustomAlert } from './CustomAlert';
import JoinRequestsModal from './JoinRequestsModal';
import GeneratePublicUrlModal from './GeneratePublicUrlModal';
import RichTextEditor from './RichTextEditor';
import MapLocationPicker from './MapLocationPicker';
import JoinCodeQr from './JoinCodeQr';

const GroupDetail = () => {
    const { user } = useContext(MyContext);
    const { groupId } = useParams();
    const navigate = useNavigate();
    const { showAlert, AlertComponent } = useCustomAlert();
    
    const [group, setGroup] = useState(null);
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userRole, setUserRole] = useState('member');
    const [isGroupCreator, setIsGroupCreator] = useState(false);
    const [showAbout, setShowAbout] = useState(false);
    const [showCreateAnnouncement, setShowCreateAnnouncement] = useState(false);
    const [topPerformers, setTopPerformers] = useState(null);
    const [loadingTopPerformers, setLoadingTopPerformers] = useState(false);
    const [showMembersModal, setShowMembersModal] = useState(false);
    const [showEditGroup, setShowEditGroup] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showJoinRequests, setShowJoinRequests] = useState(false);
    const [joinRequestsCount, setJoinRequestsCount] = useState(0);
    const [showGenerateUrl, setShowGenerateUrl] = useState(false);

    // Track views for announcements when they are first loaded
    const trackedViewsRef = useRef(new Set());

    const loadJoinRequestsCount = useCallback(async () => {
        if (userRole !== 'admin' && !isGroupCreator) return;
        
        try {
            const response = await fetch(`${backendUrl}/get_group_join_requests.php?group_id=${groupId}&user_id=${user.user_id}&status=pending`);
            const data = await response.json();
            
            if (data.success) {
                setJoinRequestsCount(data.count);
            }
        } catch (err) {
            console.error('Failed to load join requests count:', err);
        }
    }, [groupId, user.user_id, userRole, isGroupCreator]);

    useEffect(() => {
        if (group && (userRole === 'admin' || isGroupCreator)) {
            loadJoinRequestsCount();
        }
    }, [group, userRole, isGroupCreator, loadJoinRequestsCount]);

    const loadGroupData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/get_user_groups.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: user.user_id,
                    status: 'active'
                })
            });

            const data = await response.json();
            if (data.success) {
                const currentGroup = data.data.find(g => g.group_id == groupId);
                if (currentGroup) {
                    setGroup(currentGroup);
                    setUserRole(currentGroup.role);
                    // Check multiple possible fields for group creator with comprehensive type handling
                    const userId = parseInt(user.user_id);
                    const createdBy = parseInt(currentGroup.created_by);
                    
                    // Multiple comparison methods to handle different data types
                    let isCreator = false;
                    
                    // Method 1: Strict integer comparison
                    if (createdBy === userId) isCreator = true;
                    
                    // Method 2: Loose comparison (handles string vs number)
                    if (currentGroup.created_by == user.user_id) isCreator = true;
                    
                    // Method 3: String comparison
                    if (String(currentGroup.created_by) === String(user.user_id)) isCreator = true;
                    
                    // Method 4: Check other possible fields
                    if (parseInt(currentGroup.creator_id) === userId) isCreator = true;
                    if (parseInt(currentGroup.created_by_user_id) === userId) isCreator = true;
                    if (parseInt(currentGroup.user_id) === userId) isCreator = true;
                    
                    // Method 5: Check if user is admin and created the group
                    if (currentGroup.role === 'admin' && currentGroup.created_by == user.user_id) isCreator = true;
                    
                    // Method 6: Fallback - if user is admin and we can't determine creator, assume they are
                    if (!isCreator && currentGroup.role === 'admin') {
                        console.log('Fallback: User is admin, assuming they are creator');
                        isCreator = true;
                    }
                    
                    console.log('Creator detection:', {
                        userId: userId,
                        userRole: currentGroup.role,
                        createdBy: currentGroup.created_by,
                        createdByParsed: createdBy,
                        isCreator: isCreator,
                        allGroupFields: Object.keys(currentGroup)
                    });
                    
                    setIsGroupCreator(isCreator);
                    console.log('Group data:', currentGroup);
                    console.log('User ID:', user.user_id);
                    console.log('Created by:', currentGroup.created_by);
                    console.log('Creator ID:', currentGroup.creator_id);
                    console.log('Created by user ID:', currentGroup.created_by_user_id);
                    console.log('Group user ID:', currentGroup.user_id);
                    console.log('Is group creator:', isCreator);
                } else {
                    setError('Group not found or access denied');
                }
            }
        } catch (err) {
            setError('Failed to load group data');
            console.error('Error loading group data:', err);
        } finally {
            setLoading(false);
        }
    }, [user.user_id, groupId]);

    const loadAnnouncements = useCallback(async () => {
        try {
            const response = await fetch(`${backendUrl}/get_group_announcements.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    user_id: user.user_id,
                    page: 1,
                    limit: 10
                })
            });

            const data = await response.json();
            if (data.success) {
                setAnnouncements(data.data.announcements || []);
            }
        } catch (err) {
            console.error('Error loading announcements:', err);
        }
    }, [groupId, user.user_id]);

    const trackAnnouncementView = useCallback(async (announcementId) => {
        try {
            const response = await fetch(`${backendUrl}/track_announcement_view.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    announcement_id: announcementId,
                    user_id: user.user_id
                })
            });

            const data = await response.json();
            // Just track the view, don't update state to avoid infinite loop
            if (data.success && !data.data.already_viewed) {
                console.log(`View tracked for announcement ${announcementId}`);
            }
        } catch (err) {
            console.error('Error tracking announcement view:', err);
        }
    }, [user.user_id]);

    const loadTopPerformers = useCallback(async () => {
        try {
            setLoadingTopPerformers(true);
            const response = await fetch(`${backendUrl}/get_goal_top_performers.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    group_id: groupId,
                    user_id: user.user_id,
                    limit: 5
                })
            });
            const data = await response.json();
            if (data.success) {
                setTopPerformers(data.data);
            }
        } catch (err) {
            console.error('Error loading top performers:', err);
        } finally {
            setLoadingTopPerformers(false);
        }
    }, [groupId, user.user_id]);

    useEffect(() => {
        if (user && user.user_id && groupId) {
            loadGroupData();
            loadAnnouncements();
            loadTopPerformers();
        }
    }, [user.user_id, groupId, loadGroupData, loadAnnouncements, loadTopPerformers]);

    useEffect(() => {
        if (announcements.length > 0 && user && user.user_id) {
            announcements.forEach(announcement => {
                if (!trackedViewsRef.current.has(announcement.id)) {
                    trackAnnouncementView(announcement.id);
                    trackedViewsRef.current.add(announcement.id);
                }
            });
        }
    }, [announcements, user.user_id, trackAnnouncementView]);

    // Helper function to safely parse dates for Safari compatibility
    const parseDate = (dateString) => {
        if (!dateString) return null;
        
        // If it's already a Date object, return it
        if (dateString instanceof Date) return dateString;
        
        // Manual parsing for "YYYY-MM-DD HH:MM:SS" format
        const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/);
        if (match) {
            const [, year, month, day, hour, minute, second] = match;
            // Create date as local time (not UTC)
            const date = new Date(
                parseInt(year, 10),
                parseInt(month, 10) - 1, // Month is 0-indexed
                parseInt(day, 10),
                parseInt(hour, 10),
                parseInt(minute, 10),
                parseInt(second, 10)
            );
            
            return isNaN(date.getTime()) ? null : date;
        }
        
        // Fallback to regular Date parsing
        try {
            const date = new Date(dateString);
            return isNaN(date.getTime()) ? null : date;
        } catch (error) {
            console.error('Error parsing date:', dateString, error);
            return null;
        }
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        
        const date = parseDate(timestamp);
        if (!date) return 'Invalid date';
        
        const now = new Date();
        const diff = now - date;
        
        if (diff < 60000) return 'Just now';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
        if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
        if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
        
        return date.toLocaleDateString();
    };

    const getGroupTypeIcon_bk = (type) => {
        switch (type) {
            case 'church': return '⛪';
            case 'ministry': return '🙏';
            case 'community': return '👥';
            default: return '💬';
        }
    };

    const getGroupTypeIcon = (type) => {
        return '⛪';
    };

    const getGroupTypeColor = (type) => {
        switch (type) {
            case 'church': return 'text-primary';
            case 'ministry': return 'text-success';
            case 'community': return 'text-info';
            default: return 'text-secondary';
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <div className="ms-3">Loading group...</div>
            </div>
        );
    }

    if (error || !group) {
        return (
            <div className="container-fluid p-3">
                <div className="alert alert-danger" role="alert">
                    {error || 'Group not found'}
                </div>
            </div>
        );
    }

    const canCreateAnnouncements = userRole === 'admin' || isGroupCreator;

    return (
        <div className="min-vh-100 d-flex flex-column">
            <Header />
            
            <div className="container-fluid p-3 flex-grow-1" style={{ marginTop: '80px', marginBottom: '60px' }}>

                {/* Group Header */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body">
                                {/* Mobile Layout - Vertical */}
                                <div className="d-block d-md-none text-center">
                                    {/* Logo at top center */}
                                    <div className="mb-3">
                                        {group.group_photo ? (

                                                    <div  id="group-type-icon"
                                                    className="rounded-circle d-flex align-items-center justify-content-center mx-auto"
                                                    style={{ 
                                                        width: '80px', 
                                                        height: '80px', 
                                                        backgroundColor: '#e9ecef',
                                                        fontSize: '32px'
                                                    }}
                                                    >
                                                     <img 
                                                        src={group.group_photo} 
                                                        alt={group.group_name}
                                                        className="rounded-circle"
                                                        style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                                    />
                                            </div>
                                        ) : (
                                            <div  id="group-type-icon"
                                                className="rounded-circle d-flex align-items-center justify-content-center mx-auto"
                                                style={{ 
                                                    width: '80px', 
                                                    height: '80px', 
                                                    backgroundColor: '#e9ecef',
                                                    fontSize: '32px'
                                                }}
                                            >
                                                {getGroupTypeIcon(group.group_type)}
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Group name */}
                                    <h2 className="card-title mb-1">
                                        {group.group_name}
                                    </h2>
                                    
                                    {/* Group type */}
                                    <p className="text-muted mb-3 sr-only">
                                        {group.group_type.charAt(0).toUpperCase() + group.group_type.slice(1)} Group
                                    </p>
                                    
                                    {/* Members count */}
                                    <div className="mb-3">
                                        <button 
                                            className="btn btn-link p-0 text-decoration-none text-muted"
                                            onClick={() => setShowMembersModal(true)}
                                            style={{ fontSize: '0.875rem' }}
                                        >
                                            <FontAwesomeIcon icon={faUsers} className="me-1" />
                                            {group.member_count} member{group.member_count > 1 ? 's' : ''}
                                        </button>
                                    </div>
                                    
                                    {/* Admin badge and manage button */}
                                    <div className="mb-3 d-flex justify-content-center align-items-center gap-3">
                                        <span className={`badge ${userRole === 'admin' ? 'bg-danger' : 'bg-secondary'}`}>
                                            {userRole}
                                        </span>
                                        {(userRole === 'admin' || isGroupCreator) && (
                                            <div className="dropdown">
                                                <button 
                                                    className="btn btn-outline-secondary btn-sm dropdown-toggle"
                                                    type="button"
                                                    data-bs-toggle="dropdown"
                                                    aria-expanded="false"
                                                >
                                                    <FontAwesomeIcon icon={faCog} className="me-1" />
                                                    Manage
                                                </button>
                                                <ul className="dropdown-menu">
                                                    <li>
                                                        <button 
                                                            className="dropdown-item"
                                                            onClick={() => setShowEditGroup(true)}
                                                        >
                                                            <FontAwesomeIcon icon={faEdit} className="me-2" />
                                                            Edit Group
                                                        </button>
                                                    </li>
                                                    <li>
                                                        <button 
                                                            className="dropdown-item"
                                                            onClick={() => setShowJoinRequests(true)}
                                                        >
                                                            <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                                                            Join Requests
                                                            {joinRequestsCount > 0 && (
                                                                <span className="badge bg-danger ms-2">{joinRequestsCount}</span>
                                                            )}
                                                        </button>
                                                    </li>
                                                    <li>
                                                        <button 
                                                            className="dropdown-item"
                                                            onClick={() => setShowGenerateUrl(true)}
                                                        >
                                                            <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
                                                            Generate Public URL
                                                        </button>
                                                    </li>
                                                    {isGroupCreator && (
                                                        <li>
                                                            <button 
                                                                className="dropdown-item text-danger"
                                                                onClick={() => setShowDeleteConfirm(true)}
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} className="me-2" />
                                                                Delete Group
                                                            </button>
                                                        </li>
                                                    )}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Desktop Layout - Horizontal */}
                                <div className="d-none d-md-flex align-items-start">
                                    <div className="me-3">
                                        {group.group_photo ? (
                                            <img 
                                                src={group.group_photo} 
                                                alt={group.group_name}
                                                className="rounded-circle"
                                                style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <div 
                                                className="rounded-circle d-flex align-items-center justify-content-center"
                                                style={{ 
                                                    width: '80px', 
                                                    height: '80px', 
                                                    backgroundColor: '#e9ecef',
                                                    fontSize: '32px'
                                                }}
                                            >
                                                {getGroupTypeIcon(group.group_type)}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-grow-1">
                                        <h2 className="card-title mb-1 d-flex align-items-center">
                                            {group.group_name}
                                        </h2>
                                        {3> 4 && <p className="text-muted mb-2 ">
                                            {group.group_type.charAt(0).toUpperCase() + group.group_type.slice(1)} Group
                                        </p>}
                                        <div className="d-flex align-items-center gap-3">
                                            <button 
                                                className="btn btn-link p-0 text-decoration-none text-muted"
                                                onClick={() => setShowMembersModal(true)}
                                                style={{ fontSize: '0.875rem' }}
                                            >
                                                <FontAwesomeIcon icon={faUsers} className="me-1" />
                                                {group.member_count} member{group.member_count > 1 ? 's' : ''}
                                            </button>
                                            <span className={`badge ${userRole === 'admin' ? 'bg-danger' : 'bg-secondary'}`}>
                                                {userRole}
                                            </span>
                                            {(userRole === 'admin' || isGroupCreator) && (
                                                <div className="dropdown">
                                                    <button 
                                                        className="btn btn-outline-secondary btn-sm dropdown-toggle"
                                                        type="button"
                                                        data-bs-toggle="dropdown"
                                                        aria-expanded="false"
                                                    >
                                                        <FontAwesomeIcon icon={faCog} className="me-1" />
                                                        Manage
                                                    </button>
                                                    <ul className="dropdown-menu">
                                                        <li>
                                                            <button 
                                                                className="dropdown-item"
                                                                onClick={() => setShowEditGroup(true)}
                                                            >
                                                                <FontAwesomeIcon icon={faEdit} className="me-2" />
                                                                Edit Group
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button 
                                                                className="dropdown-item"
                                                                onClick={() => setShowJoinRequests(true)}
                                                            >
                                                                <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                                                                Join Requests
                                                                {joinRequestsCount > 0 && (
                                                                    <span className="badge bg-danger ms-2">{joinRequestsCount}</span>
                                                                )}
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button 
                                                                className="dropdown-item"
                                                                onClick={() => setShowGenerateUrl(true)}
                                                            >
                                                                <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
                                                                Generate Public URL
                                                            </button>
                                                        </li>
                                                        {isGroupCreator && (
                                                            <li>
                                                                <button 
                                                                    className="dropdown-item text-danger"
                                                                    onClick={() => setShowDeleteConfirm(true)}
                                                                >
                                                                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                                                                    Delete Group
                                                                </button>
                                                            </li>
                                                        )}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* About Section */}
                                <div className="mt-3">
                                    <div className="d-block d-md-none text-center">
                                        <button 
                                            className="btn btn-link p-0 text-decoration-none"
                                            onClick={() => setShowAbout(!showAbout)}
                                        >
                                            <strong>About</strong>
                                            <FontAwesomeIcon 
                                                icon={showAbout ? faChevronUp : faChevronDown} 
                                                className="ms-2" 
                                            />
                                        </button>
                                    </div>
                                    <div className="d-none d-md-block">
                                        <button 
                                            className="btn btn-link p-0 text-decoration-none"
                                            onClick={() => setShowAbout(!showAbout)}
                                        >
                                            <strong>About</strong>
                                            <FontAwesomeIcon 
                                                icon={showAbout ? faChevronUp : faChevronDown} 
                                                className="ms-2" 
                                            />
                                        </button>
                                    </div>
                                    {showAbout && (
                                        <div className="mt-2 p-3 bg-light rounded">
                                            <div 
                                                className="mb-0"
                                                dangerouslySetInnerHTML={{ __html: group.description }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Feature Cards */}
                <div className="row">
                    {/* Announcements Card */}
                    <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100">
                            <div className="card-body d-flex flex-column">
                                <div className="d-flex align-items-center mb-3">
                                    <div className="me-3">
                                        <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" 
                                             style={{ width: '50px', height: '50px' }}>
                                            <FontAwesomeIcon icon={faBell} />
                                        </div>
                                    </div>
                                    <div>
                                        <h5 className="card-title mb-0">Announcements</h5>
                                        <small className="text-muted">Latest updates</small>
                                    </div>
                                </div>
                                
                                <div className="flex-grow-1 announcements-container">
                                    {announcements.length > 0 ? (
                                        <div className="mb-3">
                                            {announcements.slice(0, 1).map((announcement) => (
                                                <div key={announcement.id} className="mb-2 p-2 bg-light rounded">
                                                    <div className="d-flex align-items-start">
                                                        {announcement.is_important && (
                                                            <FontAwesomeIcon 
                                                                icon={faBullhorn} 
                                                                className="text-warning me-2 mt-1" 
                                                            />
                                                        )}
                                                        <div className="flex-grow-1">
                                                            <h6 className="mb-1">{announcement.title}</h6>
                                                            {/* Show content only on mobile (d-md-none) */}
                                                            <div className="d-md-none">
                                                                <p className="mb-1 small text-muted">
                                                                    {announcement.message.length > 100 
                                                                        ? announcement.message.substring(0, 100) + '...'
                                                                        : announcement.message
                                                                    }
                                                                </p>
                                                                {announcement.image_url && (
                                                                    <div className="mb-2 position-relative">
                                                                        <img 
                                                                            src={announcement.image_url} 
                                                                            alt="Announcement flyer" 
                                                                            className="img-fluid rounded"
                                                                            style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'cover' }}
                                                                        />
                                                                        <div className="position-absolute top-0 end-0 m-1">
                                                                            <a 
                                                                                href={announcement.image_url} 
                                                                                download={`announcement-${announcement.id}-flyer.jpg`}
                                                                                className="btn btn-sm btn-dark"
                                                                                style={{ padding: '2px 4px', fontSize: '10px' }}
                                                                                title="Download image"
                                                                            >
                                                                                <FontAwesomeIcon icon={faDownload} />
                                                                            </a>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {announcement.has_url && (
                                                                    <a 
                                                                        href={announcement.url} 
                                                                        target="_blank" 
                                                                        rel="noopener noreferrer"
                                                                        className="btn btn-sm btn-outline-primary"
                                                                    >
                                                                        <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />
                                                                        {announcement.url_text || 'View Link'}
                                                                    </a>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <small className="text-muted">
                                                            {formatTime(announcement.created_at)}
                                                        </small>
                                                        <small className="text-muted d-flex align-items-center">
                                                            <FontAwesomeIcon icon={faEye} className="me-1" />
                                                            {announcement.view_count || 0}
                                                        </small>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-muted">No announcements yet</p>
                                    )}
                                </div>
                                
                                <div className="d-flex gap-2">
                                    <button 
                                        className="btn btn-outline-primary btn-sm flex-grow-1"
                                        onClick={() => navigate(`/group/${groupId}/announcements`)}
                                    >
                                        View All
                                    </button>
                                    {canCreateAnnouncements && (
                                        <button 
                                            className="btn btn-primary btn-sm"
                                            onClick={() => setShowCreateAnnouncement(true)}
                                        >
                                            <FontAwesomeIcon icon={faPlus} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Group Chat Card */}
                    <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100">
                            <div className="card-body d-flex flex-column">
                                <div className="d-flex align-items-center mb-3">
                                    <div className="me-3">
                                        <div className="bg-success rounded-circle d-flex align-items-center justify-content-center"
                                             style={{ width: '50px', height: '50px' }}>
                                            <FontAwesomeIcon icon={faComments} style={{ color: 'white' }} />
                                        </div>
                                    </div>
                                    <div>
                                        <h5 className="card-title mb-0">Group Chat</h5>
                                        <small className="text-muted">Connect with members</small>
                                    </div>
                                </div>

                                <div className="flex-grow-1">
                                    <p className="text-muted">Chat with group members, share messages, and stay connected.</p>
                                </div>

                                <button
                                    className="btn btn-outline-info btn-sm"
                                    onClick={() => navigate(`/group/${groupId}/chat`)}
                                    style={{ position: 'relative' }}
                                >
                                    Open Chat
                                    {Number(group.unread_count) > 0 && (
                                        <span style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minWidth: 18,
                                            height: 18,
                                            marginLeft: 8,
                                            padding: '0 5px',
                                            borderRadius: 9,
                                            background: '#e53935',
                                            color: '#fff',
                                            fontSize: 11,
                                            fontWeight: 700,
                                        }}>
                                            {Number(group.unread_count) > 99 ? '99+' : Number(group.unread_count)}
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Prayer Requests Card */}
                    <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100">
                            <div className="card-body d-flex flex-column">
                                <div className="d-flex align-items-center mb-3">
                                    <div className="me-3">
                                        <div className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center" 
                                             style={{ width: '50px', height: '50px' }}>
                                            <FontAwesomeIcon icon={faPray} />
                                        </div>
                                    </div>
                                    <div>
                                        <h5 className="card-title mb-0">Prayer Requests</h5>
                                        <small className="text-muted">Share & pray together</small>
                                    </div>
                                </div>
                                
                                <div className="flex-grow-1">
                                    <p className="text-muted">Share prayer requests and support each other in prayer.</p>
                                </div>
                                
                                <button 
                                    className="btn btn-outline-success btn-sm"
                                    onClick={() => navigate(`/group/${groupId}/prayer-requests`)}
                                >
                                    View Prayer Requests
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Counselling Card */}
                    <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100">
                            <div className="card-body d-flex flex-column">
                                <div className="d-flex align-items-center mb-3">
                                    <div className="me-3">
                                        <div className="bg-info text-white rounded-circle d-flex align-items-center justify-content-center" 
                                             style={{ width: '50px', height: '50px' }}>
                                            📞
                                        </div>
                                    </div>
                                    <div>
                                        <h5 className="card-title mb-0">Counselling</h5>
                                        <small className="text-muted">Free support</small>
                                    </div>
                                </div>
                                
                                <div className="flex-grow-1">
                                    <p className="text-muted">Get free counselling support from group counsellors.</p>
                                </div>
                                
                                <div className="d-flex gap-2">
                                    <button 
                                        className="btn btn-outline-info btn-sm flex-grow-1"
                                        onClick={() => navigate(`/group/${groupId}/counselling`)}
                                    >
                                        Start Counselling
                                    </button>
                                    {userRole === 'admin' && (
                                        <button 
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => navigate(`/group/${groupId}/counselling/admin`)}
                                            title="Manage Counsellors"
                                        >
                                            ⚙️
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Departments Card */}
                    <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100">
                            <div className="card-body d-flex flex-column">
                                <div className="d-flex align-items-center mb-3">
                                    <div className="me-3">
                                        <div className="bg-warning text-white rounded-circle d-flex align-items-center justify-content-center" 
                                             style={{ width: '50px', height: '50px' }}>
                                            <FontAwesomeIcon icon={faBuilding} />
                                        </div>
                                    </div>
                                    <div>
                                        <h5 className="card-title mb-0">Departments</h5>
                                        <small className="text-muted">Organize by teams</small>
                                    </div>
                                </div>
                                
                                <div className="flex-grow-1">
                                    <p className="text-muted">View and manage different departments within the group.</p>
                                </div>
                                
                                <div className="d-flex gap-2">
                                    <button 
                                        className="btn btn-outline-warning btn-sm flex-grow-1"
                                        onClick={() => navigate(`/group/${groupId}/departments`)}
                                    >
                                        View Departments
                                    </button>
                                    {canCreateAnnouncements && (
                                        <button 
                                            className="btn btn-warning btn-sm"
                                            onClick={() => navigate(`/group/${groupId}/departments?create=true`)}
                                        >
                                            <FontAwesomeIcon icon={faPlus} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Goals Card */}
                    <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100">
                            <div className="card-body d-flex flex-column">
                                <div className="d-flex align-items-center mb-3">
                                    <div className="me-3">
                                        <div className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center" 
                                             style={{ width: '50px', height: '50px' }}>
                                            🎯
                                        </div>
                                    </div>
                                    <div>
                                        <h5 className="card-title mb-0">Group Goals</h5>
                                        <small className="text-muted">Track & achieve objectives</small>
                                    </div>
                                </div>
                                
                                <div className="flex-grow-1">
                                    <p className="text-muted">Set goals, track progress, and celebrate achievements together.</p>
                                </div>
                                
                                <button 
                                    className="btn btn-outline-success btn-sm"
                                    onClick={() => navigate(`/group/${groupId}/goals`)}
                                >
                                    View Goals
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Top Performers Card */}
                    <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100">
                            <div className="card-body d-flex flex-column">
                                <div className="d-flex align-items-center mb-3">
                                    <div className="me-3">
                                        <div className="bg-warning text-white rounded-circle d-flex align-items-center justify-content-center" 
                                             style={{ width: '50px', height: '50px' }}>
                                            <FontAwesomeIcon icon={faTrophy} />
                                        </div>
                                    </div>
                                    <div>
                                        <h5 className="card-title mb-0">Top Performers</h5>
                                        <small className="text-muted">Goal champions</small>
                                    </div>
                                </div>
                                
                                <div className="flex-grow-1">
                                    {loadingTopPerformers ? (
                                        <div className="text-center py-3">
                                            <div className="spinner-border spinner-border-sm text-warning" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    ) : topPerformers && topPerformers.all_time_performers.length > 0 ? (
                                        <div className="top-performers-preview">
                                            {topPerformers.all_time_performers.slice(0, 3).map((performer, index) => (
                                                <div key={performer.user_id} className="d-flex align-items-center mb-2">
                                                    <div className="me-2">
                                                        {index === 0 && <FontAwesomeIcon icon={faCrown} className="text-warning" />}
                                                        {index === 1 && <FontAwesomeIcon icon={faMedal} className="text-secondary" />}
                                                        {index === 2 && <FontAwesomeIcon icon={faMedal} className="text-warning" />}
                                                        {index > 2 && <span className="badge bg-secondary">#{index + 1}</span>}
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <div className="small fw-bold">
                                                            {performer.first_name} {performer.last_name}
                                                        </div>
                                                        <div className="small text-muted">
                                                            {performer.total_completions} completions
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-muted small">No goal completions yet</p>
                                    )}
                                </div>
                                
                                <button 
                                    className="btn btn-outline-warning btn-sm"
                                    onClick={() => navigate(`/group/${groupId}/goals`)}
                                >
                                    View All Goals
                                </button>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <Footer />

            {/* Members Modal */}
            {showMembersModal && (
                <MembersModal 
                    groupId={groupId}
                    userRole={userRole}
                    isGroupCreator={isGroupCreator}
                    onClose={() => setShowMembersModal(false)}
                />
            )}

            {/* Join Requests Modal */}
            {showJoinRequests && (
                <JoinRequestsModal 
                    groupId={groupId}
                    onClose={() => {
                        setShowJoinRequests(false);
                        loadJoinRequestsCount(); // Refresh count when modal closes
                    }}
                />
            )}

            {/* Generate Public URL Modal */}
            {showGenerateUrl && (
                <GeneratePublicUrlModal 
                    groupId={groupId}
                    groupName={group?.group_name}
                    existingPublicUrl={group?.public_url}
                    onClose={() => setShowGenerateUrl(false)}
                />
            )}

            {/* Create Announcement Modal */}
            {showCreateAnnouncement && (
                <CreateAnnouncementModal 
                    groupId={groupId}
                    onClose={() => setShowCreateAnnouncement(false)}
                    onAnnouncementCreated={loadAnnouncements}
                />
            )}

            {/* Edit Group Modal */}
            {showEditGroup && (
                <EditGroupModal 
                    group={group}
                    onClose={() => setShowEditGroup(false)}
                    onGroupUpdated={loadGroupData}
                />
            )}

            {/* Delete Group Confirmation Modal */}
            {showDeleteConfirm && (
                <DeleteGroupModal 
                    group={group}
                    onClose={() => setShowDeleteConfirm(false)}
                    onGroupDeleted={() => navigate('/groups')}
                />
            )}

            {/* Custom Alert */}
            <AlertComponent />
        </div>
    );
};

// Create Announcement Modal Component
const CreateAnnouncementModal = ({ groupId, onClose, onAnnouncementCreated }) => {
    const { user } = useContext(MyContext);
    const { showAlert, AlertComponent } = useCustomAlert();
    const [formData, setFormData] = useState({
        title: '',
        message: '',
        url: '',
        url_text: '',
        is_important: false
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Prevent body scroll when modal is open
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title.trim()) {
            setError('Title is required');
            return;
        }
        
        if (!formData.message.trim()) {
            setError('Message is required');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            let imageUrl = null;
            
            // Upload image file to Cloudinary if provided
            if (imageFile) {
                const uploadFormData = new FormData();
                uploadFormData.append('image', imageFile);
                uploadFormData.append('user_id', user.user_id);
                uploadFormData.append('group_id', groupId);
                uploadFormData.append('upload_type', 'announcement');
                
                const uploadResponse = await fetch(`${backendUrl}/upload_to_cloudinary.php`, {
                    method: 'POST',
                    body: uploadFormData
                });
                
                const uploadData = await uploadResponse.json();
                
                if (uploadData.success && uploadData.data && uploadData.data.secure_url) {
                    imageUrl = uploadData.data.secure_url;
                    console.log('Image uploaded successfully:', imageUrl);
                } else {
                    console.error('Image upload failed:', uploadData);
                    setError(uploadData.messages || 'Failed to upload image');
                    return;
                }
            }

            const announcementData = {
                group_id: groupId,
                sender_id: user.user_id,
                title: formData.title.trim(),
                message: formData.message.trim(),
                url: formData.url.trim() || null,
                url_text: formData.url_text.trim() || null,
                is_important: formData.is_important ? 1 : 0,
                image_url: imageUrl
            };
            
            console.log('Sending announcement data:', announcementData);
            
            const response = await fetch(`${backendUrl}/create_group_announcement.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(announcementData)
            });

            const data = await response.json();
            console.log('Announcement creation response:', data);

            if (data.success) {
                onAnnouncementCreated();
                onClose();
            } else {
                setError(data.messages || 'Failed to create announcement');
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error creating announcement:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal show d-block" style={{ 
            backgroundColor: 'rgba(0,0,0,0.5)', 
            zIndex: 1055, 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%', 
            overflow: 'auto' 
        }}>
            <div className="modal-dialog modal-dialog-scrollable" style={{ maxHeight: '90vh', margin: '1.75rem auto' }}>
                <div className="modal-content" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                    <div className="modal-header">
                        <h5 className="modal-title">Create Announcement</h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={onClose}
                        ></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}

                            <div className="mb-3">
                                <label htmlFor="title" className="form-label">Title *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                                    placeholder="Enter announcement title"
                                    maxLength={255}
                                    required
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="message" className="form-label">Message *</label>
                                <textarea
                                    className="form-control"
                                    id="message"
                                    rows="4"
                                    value={formData.message}
                                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                                    placeholder="Enter announcement message"
                                    required
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="image" className="form-label">Flyer/Image (Optional)</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    id="image"
                                    accept="image/*"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            setImageFile(file);
                                            const reader = new FileReader();
                                            reader.onload = (e) => setImagePreview(e.target.result);
                                            reader.readAsDataURL(file);
                                        } else {
                                            setImageFile(null);
                                            setImagePreview(null);
                                        }
                                    }}
                                />
                                {imagePreview && (
                                    <div className="mt-2">
                                        <img 
                                            src={imagePreview} 
                                            alt="Preview" 
                                            className="img-thumbnail" 
                                            style={{ maxWidth: '200px', maxHeight: '200px' }}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="mb-3">
                                <label htmlFor="url" className="form-label">Link URL (Optional)</label>
                                <input
                                    type="url"
                                    className="form-control"
                                    id="url"
                                    value={formData.url}
                                    onChange={(e) => setFormData({...formData, url: e.target.value})}
                                    placeholder="https://example.com"
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="url_text" className="form-label">Link Text (Optional)</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="url_text"
                                    value={formData.url_text}
                                    onChange={(e) => setFormData({...formData, url_text: e.target.value})}
                                    placeholder="Click here to view"
                                />
                            </div>

                            <div className="mb-3">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="is_important"
                                        checked={formData.is_important}
                                        onChange={(e) => setFormData({...formData, is_important: e.target.checked})}
                                    />
                                    <label className="form-check-label" htmlFor="is_important">
                                        Mark as important
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn btn-secondary" 
                                onClick={onClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="btn btn-primary"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Creating...
                                    </>
                                ) : (
                                    'Create Announcement'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Custom Alert */}
            <AlertComponent />
        </div>
    );
};

// Members Modal Component
const MembersModal = ({ groupId, userRole, onClose, isGroupCreator }) => {
    const { user } = useContext(MyContext);
    const navigate = useNavigate();
    const { showAlert, AlertComponent } = useCustomAlert();
    
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [following, setFollowing] = useState(new Set());
    const [showAddMember, setShowAddMember] = useState(false);
    const [newMemberUsername, setNewMemberUsername] = useState('');
    const [addingMember, setAddingMember] = useState(false);
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
    const [memberToRemove, setMemberToRemove] = useState(null);
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [addingUsers, setAddingUsers] = useState(new Set());
    const [showPromoteConfirm, setShowPromoteConfirm] = useState(false);
    const [memberToPromote, setMemberToPromote] = useState(null);
    const [showDemoteConfirm, setShowDemoteConfirm] = useState(false);
    const [memberToDemote, setMemberToDemote] = useState(null);
    const [showTransferConfirm, setShowTransferConfirm] = useState(false);
    const [memberToTransfer, setMemberToTransfer] = useState(null);
    const [group, setGroup] = useState(null);
    const [showJoinQr, setShowJoinQr] = useState(false);
    const [isActualGroupCreator, setIsActualGroupCreator] = useState(false);

    const loadGroupData = useCallback(async () => {
        try {
            const response = await fetch(`${backendUrl}/get_user_groups.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: user.user_id,
                    status: 'active'
                })
            });

            const data = await response.json();
            if (data.success) {
                const currentGroup = data.data.find(g => g.group_id == groupId);
                if (currentGroup) {
                    setGroup(currentGroup);
                    // Check if current user is the actual group creator using creator username
                    const isActualOwner = currentGroup.creator_username === user.username;
                    setIsActualGroupCreator(isActualOwner);
                    
                    console.log('MembersModal - Creator detection:', {
                        userId: user.user_id,
                        userRole: currentGroup.role,
                        creatorUsername: currentGroup.creator_username,
                        currentUserUsername: user.username,
                        isActualOwner: isActualOwner,
                    });
                }
            }
        } catch (err) {
            console.error('Error loading group data:', err);
        }
    }, [user.user_id, user.username, groupId]);

    const loadMembers = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/get_group_members.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    user_id: user.user_id,
                    status: 'active'
                })
            });

            const data = await response.json();
            console.log('Members API response:', data);
            
            if (data.success) {
                // Handle the correct API response structure
                let membersData = [];
                if (data.data && data.data.members && Array.isArray(data.data.members)) {
                    membersData = data.data.members;
                } else if (Array.isArray(data.data)) {
                    membersData = data.data;
                } else {
                    console.error('Unexpected API response structure:', data);
                    setError('Unexpected response format from server');
                    return;
                }
                
                setMembers(membersData);
                console.log('Members data:', membersData);
                
                // Load follow status for each member
                const followingSet = new Set();
                for (const member of membersData) {
                    if (member.user_id !== user.user_id) {
                        try {
                            const followResponse = await fetch(`${backendUrl}/get_user_data.php`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({
                                    username: member.username,
                                    viewer: user.user_id
                                })
                            });
                            
                            const followData = await followResponse.json();
                            if (followData.is_follow) {
                                followingSet.add(member.user_id);
                            }
                        } catch (err) {
                            console.error('Error checking follow status for user:', member.user_id, err);
                        }
                    }
                }
                setFollowing(followingSet);
            } else {
                setError(data.messages || 'Failed to load members');
            }
        } catch (err) {
            setError('Failed to load members');
            console.error('Error loading members:', err);
        } finally {
            setLoading(false);
        }
    }, [groupId, user.user_id]);

    const handleFollowToggle = async (memberId, isCurrentlyFollowing) => {
        try {
            const response = await fetch(`${backendUrl}/toggle_follow.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    my_id: user.user_id,
                    profile_id: memberId
                })
            });

            const data = await response.json();
            console.log('Follow toggle response:', data);
            
            if (data.success) {
                // Update the following state based on the response
                const newFollowing = new Set(following);
                if (data.follow === false) {
                    newFollowing.delete(memberId);
                    showAlert('Unfollowed successfully', 'success');
                } else {
                    newFollowing.add(memberId);
                    showAlert('Following successfully', 'success');
                }
                setFollowing(newFollowing);
            } else {
                showAlert(data.messages || 'Failed to update follow status', 'error');
            }
        } catch (err) {
            showAlert('Network error. Please try again.', 'error');
            console.error('Error toggling follow:', err);
        }
    };

    const handleViewProfile = (member) => {
        navigate(`/${member.username}`);
    };

    const handleRemoveMember = (member) => {
        setMemberToRemove(member);
        setShowRemoveConfirm(true);
    };

    const confirmRemoveMember = async () => {
        if (!memberToRemove) return;

        try {
            const response = await fetch(`${backendUrl}/remove_group_member.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    user_id_to_remove: memberToRemove.user_id,
                    removed_by: user.user_id
                })
            });

            const data = await response.json();
            if (data.success) {
                showAlert('Member removed successfully', 'success');
                loadMembers(); // Reload the members list
            } else {
                showAlert(data.messages || 'Failed to remove member', 'error');
            }
        } catch (err) {
            showAlert('Network error. Please try again.', 'error');
            console.error('Error removing member:', err);
        } finally {
            setShowRemoveConfirm(false);
            setMemberToRemove(null);
        }
    };

    const cancelRemoveMember = () => {
        setShowRemoveConfirm(false);
        setMemberToRemove(null);
    };

    const searchUsers = async (query) => {
        if (query.length < 2) {
            setSearchResults([]);
            return;
        }

        try {
            setSearching(true);
            const response = await fetch(`${backendUrl}/search_users.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    search_query: query,
                    group_id: groupId,
                    current_user_id: user.user_id
                })
            });

            const data = await response.json();
            if (data.success) {
                setSearchResults(data.data || []);
            } else {
                setSearchResults([]);
            }
        } catch (err) {
            console.error('Error searching users:', err);
            setSearchResults([]);
        } finally {
            setSearching(false);
        }
    };

    const addUserToGroup = async (userToAdd) => {
        try {
            setAddingUsers(prev => new Set(prev).add(userToAdd.user_id));
            setError(null);

            const response = await fetch(`${backendUrl}/invite_to_group.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    invited_by: user.user_id,
                    invitation_type: 'username',
                    invitation_value: userToAdd.username
                })
            });

            const data = await response.json();
            if (data.success) {
                showAlert(`${userToAdd.username} has been added successfully`, 'success');
                setSearchResults(prev => prev.filter(u => u.user_id !== userToAdd.user_id));
                loadMembers(); // Reload the members list
            } else {
                showAlert(data.messages || `Failed to add ${userToAdd.username}`, 'error');
            }
        } catch (err) {
            showAlert('Network error. Please try again later.', 'error');
            console.error('Error adding user:', err);
        } finally {
            setAddingUsers(prev => {
                const newSet = new Set(prev);
                newSet.delete(userToAdd.user_id);
                return newSet;
            });
        }
    };

    const handlePromoteToAdmin = (member) => {
        setMemberToPromote(member);
        setShowPromoteConfirm(true);
    };

    const confirmPromoteToAdmin = async () => {
        if (!memberToPromote) return;

        try {
            const response = await fetch(`${backendUrl}/update_group_member_role.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    user_id: memberToPromote.user_id,
                    new_role: 'admin',
                    updated_by: user.user_id
                })
            });

            const data = await response.json();
            if (data.success) {
                showAlert(`${memberToPromote.first_name} ${memberToPromote.last_name} has been promoted to admin`, 'success');
                loadMembers(); // Reload the members list
            } else {
                showAlert(data.messages || 'Failed to promote member to admin', 'error');
            }
        } catch (err) {
            showAlert('Network error. Please try again.', 'error');
            console.error('Error promoting member:', err);
        } finally {
            setShowPromoteConfirm(false);
            setMemberToPromote(null);
        }
    };

    const handleDemoteFromAdmin = (member) => {
        setMemberToDemote(member);
        setShowDemoteConfirm(true);
    };

    const confirmDemoteFromAdmin = async () => {
        if (!memberToDemote) return;

        try {
            const response = await fetch(`${backendUrl}/update_group_member_role.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    user_id: memberToDemote.user_id,
                    new_role: 'member',
                    updated_by: user.user_id
                })
            });

            const data = await response.json();
            if (data.success) {
                showAlert(`${memberToDemote.first_name} ${memberToDemote.last_name} has been demoted to member`, 'success');
                loadMembers(); // Reload the members list
            } else {
                showAlert(data.messages || 'Failed to demote admin to member', 'error');
            }
        } catch (err) {
            showAlert('Network error. Please try again.', 'error');
            console.error('Error demoting member:', err);
        } finally {
            setShowDemoteConfirm(false);
            setMemberToDemote(null);
        }
    };

    const handleTransferOwnership = (member) => {
        setMemberToTransfer(member);
        setShowTransferConfirm(true);
    };

    const confirmTransferOwnership = async () => {
        if (!memberToTransfer) return;

        try {
            const response = await fetch(`${backendUrl}/transfer_group_ownership.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    new_owner_id: memberToTransfer.user_id,
                    current_owner_id: user.user_id
                })
            });

            const data = await response.json();
            if (data.success) {
                showAlert(`Group ownership has been transferred to ${memberToTransfer.first_name} ${memberToTransfer.last_name}`, 'success');
                onClose(); // Close the modal as the user is no longer the owner
            } else {
                showAlert(data.messages || 'Failed to transfer group ownership', 'error');
            }
        } catch (err) {
            showAlert('Network error. Please try again.', 'error');
            console.error('Error transferring ownership:', err);
        } finally {
            setShowTransferConfirm(false);
            setMemberToTransfer(null);
        }
    };

    const cancelPromoteAdmin = () => {
        setShowPromoteConfirm(false);
        setMemberToPromote(null);
    };

    const cancelDemoteAdmin = () => {
        setShowDemoteConfirm(false);
        setMemberToDemote(null);
    };

    const cancelTransferOwnership = () => {
        setShowTransferConfirm(false);
        setMemberToTransfer(null);
    };

    useEffect(() => {
        if (user && user.user_id && groupId) {
            loadGroupData();
            loadMembers();
        }
    }, [user.user_id, groupId, loadGroupData, loadMembers]);

    // Search users when username input changes
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (newMemberUsername.trim().length >= 2) {
                searchUsers(newMemberUsername.trim());
            } else {
                setSearchResults([]);
            }
        }, 300); // Debounce search by 300ms

        return () => clearTimeout(timeoutId);
    }, [newMemberUsername]);

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                    <div className="modal-header">
                        <h5 className="modal-title">Group Members ({members.length})</h5>
                        <div className="d-flex gap-2">
                            {userRole === 'admin' && (
                                <button 
                                    className="btn btn-primary btn-sm"
                                    onClick={() => setShowAddMember(true)}
                                >
                                    <FontAwesomeIcon icon={faUserPlus} className="me-1" />
                                    Add Member
                                </button>
                            )}
                            <button 
                                type="button" 
                                className="btn-close" 
                                onClick={onClose}
                            ></button>
                        </div>
                    </div>
                    <div className="modal-body">
                        {(userRole === 'admin' || isGroupCreator) && group?.join_code && (
                            <div className="d-flex align-items-center justify-content-between mb-3 p-3 rounded" style={{ backgroundColor: '#f4faf8' }}>
                                <div>
                                    <div className="text-muted small">Invite code</div>
                                    <div className="fw-bold" style={{ fontSize: '1.35rem', letterSpacing: '0.15em' }}>{group.join_code}</div>
                                </div>
                                <div className="d-flex gap-2">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    title="Show invite QR code"
                                    onClick={() => setShowJoinQr((open) => !open)}
                                >
                                    <FontAwesomeIcon icon={faQrcode} />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    title="Copy invite code"
                                    onClick={async () => {
                                        try {
                                            await navigator.clipboard.writeText(group.join_code);
                                            showAlert('success', 'Copied', 'Invite code copied');
                                        } catch (err) {
                                            showAlert('error', 'Copy failed', 'Could not copy the invite code');
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                </button>
                                </div>
                            </div>
                        )}
                        {showJoinQr && group?.join_code && (
                            <div className="text-center mb-3">
                                <JoinCodeQr code={group.join_code} size={200} />
                                <div className="small text-muted mt-2">Scan to open this group and request membership</div>
                            </div>
                        )}
                        {/* Add Member Form */}
                        {showAddMember && (
                            <div className="mb-4 p-3 bg-light rounded">
                                <h6 className="mb-3">Add New Member</h6>
                                <div className="position-relative">
                                    <div className="d-flex gap-2">
                                        <div className="flex-grow-1 position-relative">
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                placeholder="Search by username (min 2 characters)"
                                                value={newMemberUsername}
                                                onChange={(e) => setNewMemberUsername(e.target.value)}
                                                autoComplete="off"
                                            />
                                            
                                            {/* Search Results Dropdown */}
                                            {searchResults.length > 0 && (
                                                <div className="position-absolute w-100 bg-white border rounded shadow-sm" style={{ top: '100%', zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                                                    {searchResults.map((searchUser) => (
                                                        <div key={searchUser.user_id} className="d-flex align-items-center p-2 border-bottom">
                                                            <div className="me-2">
                                                                {searchUser.photo_url ? (
                                                                    <img 
                                                                        src={searchUser.photo_url} 
                                                                        alt={searchUser.username}
                                                                        className="rounded-circle"
                                                                        style={{ width: '30px', height: '30px', objectFit: 'cover' }}
                                                                    />
                                                                ) : (
                                                                    <div 
                                                                        className="rounded-circle d-flex align-items-center justify-content-center bg-secondary text-white"
                                                                        style={{ width: '30px', height: '30px', fontSize: '12px' }}
                                                                    >
                                                                        <FontAwesomeIcon icon={faUser} />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="flex-grow-1">
                                                                <div className="small fw-bold">{searchUser.first_name} {searchUser.last_name}</div>
                                                                <div className="small text-muted">@{searchUser.username}</div>
                                                            </div>
                                                            <button
                                                                className="btn btn-primary btn-sm"
                                                                onClick={() => addUserToGroup(searchUser)}
                                                                disabled={addingUsers.has(searchUser.user_id)}
                                                                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                                            >
                                                                {addingUsers.has(searchUser.user_id) ? (
                                                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                                                ) : (
                                                                    <FontAwesomeIcon icon={faPlus} />
                                                                )}
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            
                                            {/* Loading indicator */}
                                            {searching && (
                                                <div className="position-absolute" style={{ top: '50%', right: '10px', transform: 'translateY(-50%)' }}>
                                                    <span className="spinner-border spinner-border-sm text-muted" role="status"></span>
                                                </div>
                                            )}
                                        </div>
                                        <button 
                                            type="button" 
                                            className="btn btn-secondary btn-sm"
                                            onClick={() => {
                                                setShowAddMember(false);
                                                setNewMemberUsername('');
                                                setSearchResults([]);
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <div className="mt-2">Loading members...</div>
                            </div>
                        ) : error ? (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        ) : (
                            <div className="row">
                                {members.map((member) => (
                                    <div key={member.user_id} className="col-12 mb-3">
                                        <div className="card">
                                            <div className="card-body">
                                                <div className="d-flex align-items-center">
                                                    <div className="me-3">
                                                        {member.photo ? (
                                                            <img 
                                                                src={member.photo} 
                                                                alt={`${member.first_name} ${member.last_name}`}
                                                                className="rounded-circle"
                                                                style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                            />
                                                        ) : (
                                                            <div 
                                                                className="rounded-circle d-flex align-items-center justify-content-center bg-secondary text-white"
                                                                style={{ width: '50px', height: '50px' }}
                                                            >
                                                                <FontAwesomeIcon icon={faUser} />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                        <h6 className="mb-0 text-truncate" style={{ maxWidth: '200px' }}>
                                                            {member.first_name} {member.last_name}
                                                            {member.role === 'admin' && (
                                                                <span className="badge bg-danger ms-2">Admin</span>
                                                            )}
                                                        </h6>
                                                    </div>
                                                    <div className="d-flex gap-1 flex-wrap">
                                                        <button 
                                                            className="btn btn-outline-primary btn-sm"
                                                            onClick={() => handleViewProfile(member)}
                                                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                                        >
                                                            <FontAwesomeIcon icon={faUser} className="me-1" />
                                                            Profile
                                                        </button>
                                                        {member.user_id !== user.user_id && (
                                                            <button 
                                                                className={`btn btn-sm ${following.has(member.user_id) ? 'btn-outline-secondary' : 'btn-primary'}`}
                                                                onClick={() => handleFollowToggle(member.user_id, following.has(member.user_id))}
                                                                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                                            >
                                                                <FontAwesomeIcon 
                                                                    icon={following.has(member.user_id) ? faUserMinus : faUserPlus} 
                                                                    className="me-1" 
                                                                />
                                                                {following.has(member.user_id) ? 'Unfollow' : 'Follow'}
                                                            </button>
                                                        )}
                                                        {userRole === 'admin' && member.user_id !== user.user_id && member.username !== group?.creator_username && (
                                                            <button 
                                                                className="btn btn-outline-danger btn-sm"
                                                                onClick={() => handleRemoveMember(member)}
                                                                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                                                title="Remove member"
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        )}
                                                        {/* Group Creator Admin Management - Only actual group creator can transfer ownership */}
                                                        {isGroupCreator && member.user_id !== user.user_id && (
                                                            <>
                                                                {member.role === 'member' && (
                                                                    <button 
                                                                        className="btn btn-outline-success btn-sm"
                                                                        onClick={() => handlePromoteToAdmin(member)}
                                                                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                                                        title="Promote to admin"
                                                                    >
                                                                        <FontAwesomeIcon icon={faUserPlus} />
                                                                    </button>
                                                                )}
                                                                {member.role === 'admin' && member.username !== group?.creator_username && (
                                                                    <button 
                                                                        className="btn btn-outline-warning btn-sm"
                                                                        onClick={() => handleDemoteFromAdmin(member)}
                                                                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                                                        title="Remove admin role"
                                                                    >
                                                                        <FontAwesomeIcon icon={faUserMinus} />
                                                                    </button>
                                                                )}
                                                                {isActualGroupCreator && (
                                                                    <button 
                                                                        className="btn btn-outline-info btn-sm"
                                                                        onClick={() => handleTransferOwnership(member)}
                                                                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                                                        title="Transfer group ownership"
                                                                    >
                                                                        <FontAwesomeIcon icon={faCrown} />
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {members.length === 0 && (
                                    <div className="col-12 text-center py-4">
                                        <p className="text-muted">No members found</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button 
                            type="button" 
                            className="btn btn-secondary" 
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>

            {/* Custom Alert */}
            <AlertComponent />

            {/* Remove Member Confirmation Modal */}
            {showRemoveConfirm && memberToRemove && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
                    <div className="modal-dialog">
                        <div className="modal-content" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                            <div className="modal-header">
                                <h5 className="modal-title">Remove Member</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={cancelRemoveMember}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p>
                                    Are you sure you want to remove <strong>{memberToRemove.first_name} {memberToRemove.last_name}</strong> from this group?
                                </p>
                                <p className="text-muted small">
                                    This action cannot be undone. The member will lose access to all group content and features.
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={cancelRemoveMember}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-danger" 
                                    onClick={confirmRemoveMember}
                                >
                                    Remove Member
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Promote to Admin Confirmation Modal */}
            {showPromoteConfirm && memberToPromote && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
                    <div className="modal-dialog">
                        <div className="modal-content" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                            <div className="modal-header">
                                <h5 className="modal-title">Promote to Admin</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={cancelPromoteAdmin}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p>
                                    Are you sure you want to promote <strong>{memberToPromote.first_name} {memberToPromote.last_name}</strong> to admin?
                                </p>
                                <p className="text-muted small">
                                    Admins can manage group members, create announcements, and access admin features.
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={cancelPromoteAdmin}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-success" 
                                    onClick={confirmPromoteToAdmin}
                                >
                                    Promote to Admin
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Demote from Admin Confirmation Modal */}
            {showDemoteConfirm && memberToDemote && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
                    <div className="modal-dialog">
                        <div className="modal-content" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                            <div className="modal-header">
                                <h5 className="modal-title">Remove Admin Role</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={cancelDemoteAdmin}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p>
                                    Are you sure you want to remove admin privileges from <strong>{memberToDemote.first_name} {memberToDemote.last_name}</strong>?
                                </p>
                                <p className="text-muted small">
                                    They will become a regular member and lose admin access to group management features.
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={cancelDemoteAdmin}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-warning" 
                                    onClick={confirmDemoteFromAdmin}
                                >
                                    Remove Admin Role
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Transfer Ownership Confirmation Modal */}
            {showTransferConfirm && memberToTransfer && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
                    <div className="modal-dialog">
                        <div className="modal-content" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                            <div className="modal-header">
                                <h5 className="modal-title">Transfer Group Ownership</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={cancelTransferOwnership}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p>
                                    Are you sure you want to transfer group ownership to <strong>{memberToTransfer.first_name} {memberToTransfer.last_name}</strong>?
                                </p>
                                <p className="text-muted small">
                                    <strong>Warning:</strong> This action cannot be undone. You will lose all owner privileges and become a regular member. The new owner will have full control over the group.
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={cancelTransferOwnership}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-danger" 
                                    onClick={confirmTransferOwnership}
                                >
                                    Transfer Ownership
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Edit Group Modal Component
const EditGroupModal = ({ group, onClose, onGroupUpdated }) => {
    const { user } = useContext(MyContext);
    const { showAlert, AlertComponent } = useCustomAlert();
    const [formData, setFormData] = useState({
        group_name: group?.group_name || '',
        description: group?.description || '',
        group_type: group?.group_type || 'general',
        address: group?.address || '',
        latitude: group?.latitude != null ? Number(group.latitude) : null,
        longitude: group?.longitude != null ? Number(group.longitude) : null
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(group?.group_photo || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Prevent body scroll when modal is open
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    useEffect(() => {
        if (group) {
            setFormData((prev) => ({
                ...prev,
                group_name: group.group_name || '',
                description: group.description || '',
                group_type: group.group_type || 'general',
                address: group.address || '',
                latitude: group.latitude != null ? Number(group.latitude) : null,
                longitude: group.longitude != null ? Number(group.longitude) : null
            }));
        }
    }, [group]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.group_name.trim()) {
            setError('Group name is required');
            return;
        }
        
        // Strip HTML tags for validation
        const plainTextDescription = formData.description.replace(/<[^>]*>/g, '');
        
        if (!plainTextDescription.trim()) {
            setError('Description is required');
            return;
        }
        
        if (plainTextDescription.length < 10) {
            setError('Description must be at least 10 characters long');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            let imageUrl = group.group_photo; // Keep existing image by default
            
            // Upload new image file to Cloudinary if provided
            if (imageFile) {
                const uploadFormData = new FormData();
                uploadFormData.append('image', imageFile);
                uploadFormData.append('user_id', user.user_id);
                uploadFormData.append('group_id', group.group_id);
                uploadFormData.append('upload_type', 'group');
                
                const uploadResponse = await fetch(`${backendUrl}/upload_to_cloudinary.php`, {
                    method: 'POST',
                    body: uploadFormData
                });
                
                const uploadData = await uploadResponse.json();
                
                if (uploadData.success && uploadData.data && uploadData.data.secure_url) {
                    imageUrl = uploadData.data.secure_url;
                    console.log('Group logo updated successfully:', imageUrl);
                } else {
                    console.error('Group logo upload failed:', uploadData);
                    setError(uploadData.messages || 'Failed to upload group logo');
                    return;
                }
            }

            const payload = {
                user_id: user.user_id,
                group_id: group.group_id,
                group_name: formData.group_name.trim(),
                description: formData.description.trim(),
                group_photo: imageUrl,
                group_type: formData.group_type || 'general'
            };
            if (formData.address != null && String(formData.address).trim() !== '') payload.address = formData.address.trim();
            if (formData.latitude != null && !Number.isNaN(Number(formData.latitude))) payload.latitude = Number(formData.latitude);
            if (formData.longitude != null && !Number.isNaN(Number(formData.longitude))) payload.longitude = Number(formData.longitude);

            const response = await fetch(`${backendUrl}/update_group.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.success) {
                showAlert('success', 'Success', 'Group updated successfully');
                onGroupUpdated();
                onClose();
            } else {
                setError(data.messages || 'Failed to update group');
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error updating group:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055, position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', overflow: 'auto' }}>
            <div className="modal-dialog modal-dialog-scrollable" style={{ maxHeight: '90vh', margin: '1.75rem auto' }}>
                <div className="modal-content" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                    <div className="modal-header">
                        <h5 className="modal-title">Edit Group</h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={onClose}
                        ></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}

                            <div className="mb-3">
                                <label htmlFor="group_name" className="form-label">Group Name *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="group_name"
                                    value={formData.group_name}
                                    onChange={(e) => setFormData({...formData, group_name: e.target.value})}
                                    placeholder="Enter group name"
                                    maxLength={255}
                                    required
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="edit_group_type" className="form-label">Group Category</label>
                                <select
                                    className="form-select"
                                    id="edit_group_type"
                                    value={formData.group_type || 'general'}
                                    onChange={(e) => setFormData({ ...formData, group_type: e.target.value })}
                                >
                                    <option value="general">General</option>
                                    <option value="church">Church</option>
                                    <option value="ministry">Ministry</option>
                                    <option value="community">Community</option>
                                </select>
                            </div>

                            <div className="mb-3">
                                <label htmlFor="description" className="form-label">Description *</label>
                                <RichTextEditor
                                    id="edit-group-description"
                                    value={formData.description}
                                    onChange={(content) => setFormData({...formData, description: content})}
                                    placeholder="Describe your group"
                                    height={200}
                                    maxLength={1000}
                                />
                            </div>

                            {formData.group_type === 'church' && (
                                <div className="mb-3">
                                    <label className="form-label">Address &amp; map location</label>
                                    <p className="small text-muted">Set address and map pointer so this church appears in Find a church near you.</p>
                                    <MapLocationPicker
                                        initialAddress={formData.address || ''}
                                        initialLatitude={formData.latitude}
                                        initialLongitude={formData.longitude}
                                        onLocationSelect={({ address, latitude, longitude }) => {
                                            setFormData((prev) => ({
                                                ...prev,
                                                address: address != null ? address : prev.address,
                                                latitude: latitude != null ? latitude : prev.latitude,
                                                longitude: longitude != null ? longitude : prev.longitude
                                            }));
                                        }}
                                    />
                                </div>
                            )}

                            <div className="mb-3">
                                <label htmlFor="group_logo" className="form-label">Group Logo</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    id="group_logo"
                                    accept="image/*"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            setImageFile(file);
                                            const reader = new FileReader();
                                            reader.onload = (e) => setImagePreview(e.target.result);
                                            reader.readAsDataURL(file);
                                        } else {
                                            setImageFile(null);
                                            setImagePreview(group.group_photo);
                                        }
                                    }}
                                />
                                {imagePreview && (
                                    <div className="mt-2">
                                        <img 
                                            src={imagePreview} 
                                            alt="Group logo preview" 
                                            className="img-thumbnail" 
                                            style={{ maxWidth: '150px', maxHeight: '150px' }}
                                        />
                                    </div>
                                )}
                                <div className="form-text">
                                    Upload a new logo or keep the current one.
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn btn-secondary" 
                                onClick={onClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="btn btn-primary"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Updating...
                                    </>
                                ) : (
                                    'Update Group'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Custom Alert */}
            <AlertComponent />
        </div>
    );
};

// Delete Group Modal Component
const DeleteGroupModal = ({ group, onClose, onGroupDeleted }) => {
    const { user } = useContext(MyContext);
    const { showAlert, showConfirm, AlertComponent } = useCustomAlert();
    const [loading, setLoading] = useState(false);

    const handleDelete = () => {
        showConfirm(
            'Delete Group',
            `Are you sure you want to delete "${group.group_name}"? This action cannot be undone and will remove all group data, members, and content.`,
            async () => {
                try {
                    setLoading(true);
                    
                    const response = await fetch(`${backendUrl}/delete_group.php`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            user_id: user.user_id,
                            group_id: group.group_id
                        })
                    });

                    const data = await response.json();

                    if (data.success) {
                        showAlert('success', 'Success', 'Group deleted successfully');
                        onGroupDeleted();
                    } else {
                        showAlert('error', 'Error', data.messages || 'Failed to delete group');
                    }
                } catch (err) {
                    showAlert('error', 'Error', 'Network error. Please try again later.');
                    console.error('Error deleting group:', err);
                } finally {
                    setLoading(false);
                }
            },
            {
                type: 'danger',
                confirmText: 'Delete Group',
                cancelText: 'Cancel'
            }
        );
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055, position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', overflow: 'auto' }}>
            <div className="modal-dialog modal-dialog-scrollable" style={{ maxHeight: '90vh', margin: '1.75rem auto' }}>
                <div className="modal-content" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                    <div className="modal-header">
                        <h5 className="modal-title text-danger">Delete Group</h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={onClose}
                        ></button>
                    </div>
                    <div className="modal-body" style={{ overflowY: 'auto', maxHeight: 'calc(90vh - 120px)' }}>
                        <div className="alert alert-danger" role="alert">
                            <h6 className="alert-heading">⚠️ Warning: This action cannot be undone!</h6>
                            <p className="mb-0">
                                Deleting <strong>"{group.group_name}"</strong> will permanently remove:
                            </p>
                            <ul className="mt-2 mb-0">
                                <li>All group messages and announcements</li>
                                <li>All group members and their data</li>
                                <li>All departments and goals</li>
                                <li>All prayer requests and testimonies</li>
                                <li>All group settings and configurations</li>
                            </ul>
                        </div>
                        <p className="text-muted">
                            Only the group creator can delete a group. All members will be notified and lose access immediately.
                        </p>
                    </div>
                    <div className="modal-footer">
                        <button 
                            type="button" 
                            className="btn btn-secondary" 
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button 
                            type="button" 
                            className="btn btn-danger"
                            onClick={handleDelete}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    Deleting...
                                </>
                            ) : (
                                'Delete Group'
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Custom Alert */}
            <AlertComponent />
        </div>
    );
};

export default GroupDetail;
